import { DICIONARIO_EQUIPES, normalizar } from './palavrasEquipes';
import type { EquipePlacar, RespostaFicha } from './types';

export interface SugestaoEquipe {
  equipeId: number;
  nome: string;
  estrelas: number;
  frases: string[];
  preferida: boolean;
}

// Perguntas do tipo "em qual equipe você gostaria de servir?".
const PREFERENCIA_REGEX =
  /em qual equipe|qual equipe|gostaria de servir|equipe.*servir|servir.*equipe/i;

function valoresTexto(r: RespostaFicha): string[] {
  if (Array.isArray(r.resposta)) return r.resposta.filter(Boolean).map((v) => String(v));
  if (r.resposta === null || r.resposta === undefined) return [];
  if (typeof r.resposta === 'boolean') return [r.resposta ? 'Sim' : 'Não'];
  return [String(r.resposta)];
}

function dicionarioDaEquipe(nomeEquipe: string) {
  const n = normalizar(nomeEquipe);
  return DICIONARIO_EQUIPES.find((d) => n.includes(normalizar(d.id)));
}

// Calcula as equipes com sinal positivo a partir do que a pessoa escreveu.
// É só leitura da ficha — nada vai para o banco e nada é comparado entre pessoas.
export function calcularSugestoes(
  respostas: RespostaFicha[],
  equipes: EquipePlacar[],
): SugestaoEquipe[] {
  const respondidas = respostas.filter((r) => r.respondeu);
  if (respondidas.length === 0) return [];

  const pares = respondidas.flatMap((r) =>
    valoresTexto(r).map((valor) => ({ pergunta: r.pergunta, valor })),
  );
  const textoTotal = normalizar(pares.map((p) => p.valor).join(' '));

  // Equipes apontadas diretamente numa pergunta de preferência.
  const preferidas = new Set<number>();
  for (const r of respondidas) {
    if (!PREFERENCIA_REGEX.test(normalizar(r.pergunta))) continue;
    for (const v of valoresTexto(r)) {
      const vn = normalizar(v);
      for (const eq of equipes) {
        const d = dicionarioDaEquipe(eq.equipe);
        if (!d) continue;
        if (vn.includes(normalizar(d.id)) || vn.includes(normalizar(eq.equipe))) {
          preferidas.add(eq.equipe_id);
        }
      }
    }
  }

  const sugestoes: SugestaoEquipe[] = [];
  for (const eq of equipes) {
    const d = dicionarioDaEquipe(eq.equipe);
    if (!d) continue;

    const encontradas = new Set<string>();
    const frases: string[] = [];
    for (const p of pares) {
      const vn = normalizar(p.valor);
      let bateu = false;
      for (const palavra of d.palavras) {
        const pn = normalizar(palavra);
        if (pn && vn.includes(pn)) {
          encontradas.add(pn);
          bateu = true;
        }
      }
      if (bateu) frases.push(p.valor.trim());
    }

    const contagem = encontradas.size;
    if (contagem === 0) continue;

    let estrelas = contagem >= 3 ? 5 : contagem === 2 ? 4 : 3;

    // Nome da equipe escrito por extenso sobe direto para o topo.
    const nomePorExtenso =
      textoTotal.includes(normalizar(d.id)) || textoTotal.includes(normalizar(eq.equipe));
    if (nomePorExtenso) estrelas = 5;

    const preferida = preferidas.has(eq.equipe_id);
    if (preferida) estrelas = 5;

    sugestoes.push({
      equipeId: eq.equipe_id,
      nome: d.nome,
      estrelas,
      frases: Array.from(new Set(frases)),
      preferida,
    });
  }

  sugestoes.sort((a, b) => {
    if (a.preferida !== b.preferida) return a.preferida ? -1 : 1;
    return b.estrelas - a.estrelas;
  });

  return sugestoes.slice(0, 3);
}